import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { invariant } from 'shared/utils/utils';
import { RenderUnsettledUI } from 'shared/components';
import {
  ServiceAgreementDetailResponse,
  ServiceAgreementState,
} from 'ogp/services/service-agreements.types';
import { Box, Typography } from '@mui/material';
import { Header, Page, Toolbox } from 'redesign';
import { ServiceAgreementStateChip } from './service-agreement-state-chip';
import { ServiceAgreementDetailTabs } from './tabs/service-agreement-detail-tabs';
import { ServiceAgreementDetailToolbox } from './toolbox/service-agreement-detail-toolbox';
import { useGetServiceAgreementDetail } from '../../hooks/use-get-service-agreement-detail';

export const ServiceAgreementDetailDataProvider = () => {
  const { id } = useParams<'id'>();
  invariant(id);

  const serviceAgreementQuery = useGetServiceAgreementDetail(id);

  if (serviceAgreementQuery.status !== 'success') {
    return <RenderUnsettledUI data={serviceAgreementQuery} />;
  }

  return <ServiceAgreementDetail serviceAgreement={serviceAgreementQuery.data} />;
};

const ServiceAgreementDetail = ({
  serviceAgreement,
}: {
  serviceAgreement: ServiceAgreementDetailResponse;
}) => {
  const { t } = useTranslation(['ogp-screen-specific', 'all-platforms-common']);
  const roleLabels = {
    LENDER: t('service-agreement.lender', { ns: 'all-platforms-common' }),
    BORROWER: t('service-agreement.borrower', { ns: 'all-platforms-common' }),
  };
  const serviceAgreementRoleTranslation = roleLabels[serviceAgreement.serviceAgreementPartyRole];
  const serviceAgreementSubheaderTranslation = t('service-agreement.subheader', {
    role: serviceAgreementRoleTranslation,
    ns: 'ogp-screen-specific',
  });
  const serviceAgreementSubheaderElement = (
    <Box display="flex" alignItems="center" gap={2}>
      <Typography variant="subtitle1" color="secondary" noWrap>
        {serviceAgreementSubheaderTranslation}
      </Typography>
      <ServiceAgreementStateChip type={serviceAgreement.state as ServiceAgreementState} />
    </Box>
  );
  const serviceAgreementName = `${serviceAgreement.lenderServiceAgreementParty.name} - ${serviceAgreement.borrowerServiceAgreementParty.name}`;
  return (
    <Page
      header={
        <Header titleText={serviceAgreementName} subheaderText={serviceAgreementSubheaderElement} />
      }
      toolbox={
        <Toolbox>
          <ServiceAgreementDetailToolbox
            serviceAgreementId={serviceAgreement.id}
            viewerRole={serviceAgreement.serviceAgreementPartyRole}
            state={serviceAgreement.state}
          />
        </Toolbox>
      }
    >
      <Box width="100%">
        <ServiceAgreementDetailTabs data={serviceAgreement} />
      </Box>
    </Page>
  );
};
